import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../utils/firebase"; // Direct import for listener
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  getDocs,
} from "firebase/firestore";
import {
  fetchOrders,
  updateOrderStatus,
  getRestaurantProfile,
  updateOrderItems,
  placeOrder,
} from "../utils/api";
import { v4 as uuidv4 } from "uuid";
import { FaPlus, FaFileCsv, FaPrint } from "react-icons/fa";
//import { formatDistanceToNow } from 'date-fns';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ParcelOrderModal from "../components/ParcelOrderModal";

function StatusBadge({ status }) {
  const statusColors = {
    completed: "bg-green-100 text-green-800",
    pending: "bg-amber-100 text-amber-800",
    cancelled: "bg-rose-100 text-rose-800",
    preparing: "bg-blue-100 text-blue-800",
    served: "bg-purple-100 text-purple-800",
  };

  const formattedStatus =
    status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  const colorClass =
    statusColors[status.toLowerCase()] || "bg-gray-100 text-gray-800";

  return (
    <motion.span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${colorClass}`}
      whileHover={{ scale: 1.05 }}
    >
      {formattedStatus}
    </motion.span>
  );
}

function formatBillLine(
  desc,
  qty,
  amt,
  descWidth = 18,
  qtyWidth = 4,
  amtWidth = 8
) {
  // Pad or trim description
  let d = desc.length > descWidth ? desc.slice(0, descWidth - 1) + "." : desc;
  d = d.padEnd(descWidth, " ");
  let q = qty
    ? String(qty).padStart(qtyWidth, " ")
    : "".padStart(qtyWidth, " ");
  let a = amt
    ? String(amt).padStart(amtWidth, " ")
    : "".padStart(amtWidth, " ");
  return `${d}${q}${a}`;
}

// ✅ UPDATED formatBill() with strict widths and truncation

function formatBill(order, restaurantName) {
  // 80mm paper is about 48 chars wide in monospace
  const width = 48;
  const descW = 14,
    qtyW = 4,
    priceW = 7,
    amtW = 8;
  let lines = [];

  // Centered Restaurant Name
  const nameLine = (restaurantName || "RESTAURANT").toUpperCase();
  lines.push(nameLine.padStart(Math.floor((width + nameLine.length) / 2), " "));
  lines.push("");

  // Bill Info Block
  const billNo = `Order No: ${
    order.billNumber || (order.id ? order.id.slice(-5) : "-")
  }`;
  const table = `Table: ${order.tableName || order.tableId}`;
  lines.push((billNo + "   " + table).padEnd(width, " "));
  const date = `Date: ${getCurrentIndianDate()}`;
  const time = `Time: ${getCurrentIndianTime()}`;
  lines.push((date + "   " + time).padEnd(width, " "));
  lines.push("");

  // Separator
  lines.push("-".repeat(width));
  // Table Header (shorter names)
  let header =
    "Item".padEnd(descW, " ") +
    "Qty".padStart(qtyW, " ") +
    "Rate".padStart(priceW, " ") +
    "Amt".padStart(amtW, " ");
  lines.push(header);
  lines.push("-".repeat(width));

  // Items
  order.items.forEach((item) => {
    let itemName =
      item.name.length > descW
        ? item.name.slice(0, descW - 1) + "."
        : item.name;
    let qty = String(item.qty).padStart(qtyW, " ");
    let price = Number(item.price).toFixed(2).padStart(priceW, " ");
    let amt = (Number(item.price) * item.qty).toFixed(2).padStart(amtW, " ");
    lines.push(itemName.padEnd(descW, " ") + qty + price + amt);
  });

  // Separator
  lines.push("-".repeat(width));

  // Totals with optional discount
  const subtotal =
    order.subtotal != null
      ? Number(order.subtotal)
      : Math.round(
          (order.items || []).reduce(
            (sum, i) => sum + Number(i.price) * (i.qty || 1),
            0
          ) * 100
        ) / 100;
  const discountPercent = Number(order.discountPercent || 0);
  const discountAmount =
    Math.round(subtotal * (discountPercent / 100) * 100) / 100;
  const adjusted = Math.round((subtotal - discountAmount) * 100) / 100;
  const tax = Math.round(adjusted * 0.05 * 100) / 100;
  const grandTotal = Math.round((adjusted + tax) * 100) / 100;
  // Calculate where the amount column starts
  const amountColStart = descW + qtyW + priceW;
  lines.push(
    "Subtotal:".padEnd(amountColStart, " ") +
      subtotal.toFixed(2).padStart(amtW, " ")
  );
  if (discountPercent > 0) {
    lines.push(
      `Discount (${discountPercent}%):`.padEnd(amountColStart, " ") +
        ("-" + discountAmount.toFixed(2)).padStart(amtW, " ")
    );
  }
  lines.push(
    "Tax (5%):".padEnd(amountColStart, " ") + tax.toFixed(2).padStart(amtW, " ")
  );
  lines.push(
    "Grand Total:".padEnd(amountColStart, " ") +
      grandTotal.toFixed(2).padStart(amtW, " ")
  );
  lines.push("-".repeat(width));
  lines.push("");

  // Thank You message centered
  const thankYou = "Thank You, Please Visit again";
  lines.push(thankYou.padStart(Math.floor((width + thankYou.length) / 2), " "));

  return lines.join("\n");
}

function InvoiceModal({ order, onClose, restaurantName, onAfterPrint }) {
  const printRef = useRef();
  if (!order) return null;
  const subtotal = order.total;
  const tax = Math.round(subtotal * 0.05 * 100) / 100;
  const grandTotal = Math.round((subtotal + tax) * 100) / 100;

  // Print handler for browser print
  const handlePrint = () => {
    if (!printRef.current) return;
    const pre = printRef.current.querySelector("pre");
    const printContents = pre ? pre.outerHTML : printRef.current.innerHTML;
    const win = window.open("", "", "width=400,height=600");
    win.document.write("<html><head><title>Invoice</title>");
    win.document.write(
      "<style>body{font-family:monospace;font-size:12px;} pre{margin:0;padding:0;white-space:pre-wrap;}</style>"
    );
    win.document.write("</head><body>");
    win.document.write(printContents);
    win.document.write("</body></html>");
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.close();
      onClose();
      if (onAfterPrint) onAfterPrint();
    }, 200);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{ cursor: "pointer" }}
    >
      <div
        className="bg-white rounded p-2 w-full max-w-[320px] shadow-xl relative text-xs font-mono receipt-print"
        style={{ minWidth: 260, cursor: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-2 right-2 text-gray-400 no-print"
          onClick={onClose}
        >
          &times;
        </button>
        {/* Printable content */}
        <div ref={printRef}>
          <pre
            className="text-xs font-mono leading-4 whitespace-pre-wrap mb-2"
            style={{ margin: 0, padding: 0 }}
          >
            {formatBill(order, restaurantName)}
          </pre>
        </div>
        {/* Print Button (browser) */}
        <button
          className="mt-2 w-full py-1 bg-amber-500 text-white rounded text-xs font-semibold no-print"
          disabled={!order}
          onClick={handlePrint}
        >
          Print
        </button>
      </div>
    </div>
  );
}
const BASE_URL = process.env.REACT_APP_API_BASE_URL_IMG;

// function NewOrderReceivedModal({ order, onClose }) {
//   if (!order) return null;

//   return (
//     <motion.div
//       className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       exit={{ opacity: 0 }}
//       onClick={(e) => {
//         if (e.target === e.currentTarget) onClose();
//       }}
//     >
//       <motion.div
//         className="bg-white rounded-2xl shadow-2xl w-full max-w-sm text-center p-6"
//         initial={{ y: 20, scale: 0.95 }}
//         animate={{ y: 0, scale: 1 }}
//         exit={{ y: 20, scale: 0.95 }}
//       >
//         <motion.div
//           className="text-5xl mb-4"
//           animate={{ scale: [1, 1.1, 1] }}
//           transition={{ duration: 0.5, repeat: Infinity }}
//         >
//           🎉
//         </motion.div>
//         <h2 className="text-2xl font-bold text-gray-800 mb-2">
//           New Order Received!
//         </h2>
//         <p className="text-gray-600">
//           Order <strong>#{order.id.slice(-5)}</strong> for{" "}
//           <strong>{order.tableName}</strong> has been placed.
//         </p>
//         <button
//           onClick={onClose}
//           className="mt-6 w-full py-2 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all"
//         >
//           Acknowledge
//         </button>
//       </motion.div>
//     </motion.div>
//   );
//}

// Helper to get full image URL (same logic as in Cart and CustomerTable)
function getFullImageUrl(imageUrl) {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("http")) return imageUrl;
  const cleanUrl = imageUrl.replace(/^\/?.*uploads\//, "");
  return `${BASE_URL}/uploads/${cleanUrl}`;
}

// CSV Export helper
function exportOrdersToCSV(orders) {
  const headers = ["Order ID", "Table", "Status", "Total", "Date"];
  const rows = orders.map((order) => [
    order.id.slice(-5),
    order.tableName || `Table ${order.tableId}`,
    order.status,
    `₹${Math.round(order.total * 1.05 * 100) / 100}`,
    order.createdAt && order.createdAt._seconds
      ? new Date(order.createdAt._seconds * 1000).toLocaleString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
          timeZone: "Asia/Kolkata",
        })
      : "",
  ]);
  let csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");
  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `orders-${new Date().toISOString().slice(0, 10)}.csv`
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// Helper to robustly get order date
function getOrderDate(createdAt) {
  if (!createdAt) return "";
  if (createdAt._seconds) {
    // Firestore Timestamp object
    return new Date(createdAt._seconds * 1000).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  }
  if (typeof createdAt === "number") {
    // Unix timestamp in seconds
    return new Date(createdAt * 1000).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  }
  if (typeof createdAt === "string") {
    // ISO string
    return new Date(createdAt).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  }
  return "";
}

// Helper to get time string
function getOrderTime(createdAt) {
  if (!createdAt) return "";
  let dateObj;
  if (createdAt._seconds) dateObj = new Date(createdAt._seconds * 1000);
  else if (typeof createdAt === "number") dateObj = new Date(createdAt * 1000);
  else if (typeof createdAt === "string") dateObj = new Date(createdAt);
  else return "";
  return dateObj.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

// Helper to get UTC start and end of day for a Date object
function getUTCStartOfDay(date) {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
  );
}
function getUTCEndOfDay(date) {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999
    )
  );
}

// Helper to get current Indian date
function getCurrentIndianDate() {
  const now = new Date();
  return now.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}
// Helper to get current Indian time
function getCurrentIndianTime() {
  const now = new Date();
  return now.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

export default function Orders({ restaurantId }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [showParcelModal, setShowParcelModal] = useState(false);
  const [restaurantName, setRestaurantName] = useState("");
  const [newOrderModal, setNewOrderModal] = useState(null);
  const isInitialLoad = useRef(true);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  });
  const [endDate, setEndDate] = useState(() => new Date());
  const [showPrintModal, setShowPrintModal] = useState(false);
  // Show More/Less state
  const [visibleCount, setVisibleCount] = useState(10);
  const audioUnlockedRef = useRef(false);

  useEffect(() => {
    if (!restaurantId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Set up real-time listener for orders
    const q = query(
      collection(db, `restaurants/${restaurantId}/orders`),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => {
        // Normalize and coerce numeric fields so invoice code always sees subtotal / discount fields
        const ordersData = querySnapshot.docs.map((doc) => {
          const data = doc.data() || {};
          const items = data.items || [];

          // compute authoritative subtotal from items (2-decimal)
          const computedSubtotal =
            Math.round(
              (items.reduce(
                (s, it) => s + Number(it.price) * (it.qty || 1),
                0
              ) || 0) * 100
            ) / 100;

          // compute discount/tax/total from items + discountPercent
          const dPercent = Number(data.discountPercent || 0);
          const computedDiscountAmount =
            Math.round(computedSubtotal * (dPercent / 100) * 100) / 100;
          const computedTotalBeforeTax =
            Math.round((computedSubtotal - computedDiscountAmount) * 100) / 100;
          // store values that UI and invoice will use (always use computed values)
          const subtotal = computedSubtotal;
          const discountPercent = dPercent;
          const discountAmount = computedDiscountAmount;
          const total = computedTotalBeforeTax;

          return {
            id: doc.id,
            ...data,
            items,
            subtotal,
            discountPercent,
            discountAmount,
            total,
            createdAt: data.createdAt,
          };
        });

        // Get table names to enrich orders data
        const tablesSnap = await getDocs(
          collection(db, `restaurants/${restaurantId}/tables`)
        );
        const tableMap = {};
        tablesSnap.forEach((doc) => {
          tableMap[doc.id] = doc.data().name || doc.id;
        });

        const enrichedOrders = ordersData.map((order) => ({
          ...order,
          // If this is a room order server may not set tableName — prefer roomNumber
          tableName:
            order.roomNumber
              ? `Room ${order.roomNumber}`
              : tableMap[order.tableId] || order.tableName || order.tableId,
        }));

        // Check for new orders after initial load
        if (isInitialLoad.current) {
          isInitialLoad.current = false;
        } else {
          querySnapshot.docChanges().forEach((change) => {
            if (change.type === "added") {
              const newOrder = enrichedOrders.find(
                (o) => o.id === change.doc.id
              );
              console.log(
                "New order received:",
                newOrder,
                "Source:",
                newOrder?.source
              );
              setNewOrderModal(newOrder); // Show modal for the new order
              if (
                newOrder &&
                newOrder.source === "customer" &&
                audioUnlockedRef.current
              ) {
                console.log("Playing new order sound...");
                new Audio("/assets/NewOrderReceived.mp3")
                  .play()
                  .catch((e) => console.error("Error playing sound:", e));
              } else {
                console.log(
                  "Audio not played: order not from customer or audio not unlocked."
                );
              }
            }
          });
        }

        setOrders(enrichedOrders);
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to orders:", err);
        setError("Failed to connect to real-time orders feed.");
        setLoading(false);
      }
    );

    // Fetch restaurant name separately
    getRestaurantProfile(restaurantId)
      .then((profile) => setRestaurantName(profile.name || ""))
      .catch((err) => console.error("Failed to load restaurant name", err));

    // Cleanup listener on component unmount
    return () => unsubscribe();
  }, [restaurantId]);

  // Date range filter fetch
  useEffect(() => {
    if (!restaurantId) return;
    if (startDate && endDate) {
      (async () => {
        setLoading(true);
        try {
          // Always send full UTC day range
          const utcStart = getUTCStartOfDay(startDate);
          const utcEnd = getUTCEndOfDay(endDate);
          const orders = await fetchOrders(
            restaurantId,
            utcStart.toISOString(),
            utcEnd.toISOString()
          );
          setOrders(orders);
        } catch (err) {
          setError("Failed to fetch filtered orders");
        }
        setLoading(false);
      })();
    }
  }, [startDate, endDate, restaurantId]);

  // Fetch all orders if no date filter is set
  useEffect(() => {
    if (!restaurantId) return;
    if (!startDate && !endDate) {
      (async () => {
        setLoading(true);
        try {
          const orders = await fetchOrders(restaurantId);
          setOrders(orders);
        } catch (err) {
          setError("Failed to fetch orders");
        }
        setLoading(false);
      })();
    }
  }, [restaurantId, startDate, endDate]);

  // Search filter (fix table search to exact number)
  const filteredOrders = (
    filter === "all"
      ? orders
      : orders.filter(
          (order) => order.status.toLowerCase() === filter.toLowerCase()
        )
  ).filter((order) => {
    if (!search) return true;
    const idMatch = order.id.toLowerCase().includes(search.toLowerCase());
    // Extract table number from tableName (e.g., 'Table 9')
    const tableNum = (order.tableName || "")
      .toLowerCase()
      .match(/table\s*(\d+)/);
    const searchNum = search.trim().toLowerCase().replace("table", "").trim();
    const tableExact = tableNum && searchNum && tableNum[1] === searchNum;
    // Also allow full table name match
    const tableNameExact =
      (order.tableName || "").toLowerCase() === search.trim().toLowerCase();
    return idMatch || tableExact || tableNameExact;
  });

  // Only show up to visibleCount orders
  const visibleOrders = filteredOrders.slice(0, visibleCount);

  const statusCounts = orders.reduce((acc, order) => {
    const status = order.status.toLowerCase();
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  async function handleMarkCompleted(orderId) {
    // No longer need to manually reload data, listener will catch the update
    await updateOrderStatus(restaurantId, orderId, "completed");
  }

  async function handleUpdateOrder(newItems) {
    if (!updatingOrder) return;
    try {
      await updateOrderItems(restaurantId, updatingOrder.id, newItems);
      setUpdatingOrder(null);
      // No reload needed
    } catch (err) {
      console.error("Failed to update order:", err);
      alert("Failed to update order.");
    }
  }

  async function handlePlaceParcelOrder(
    items,
    type,
    input,
    discountPercent = 0,
    source = "staff"
  ) {
    try {
      await placeOrder({
        restaurantId,
        items,
        tableId:
          type === "table"
            ? `table-${input}`
            : type === "room"
            ? `room-${input}`
            : `parcel-${uuidv4().slice(0, 8)}`,
        tableName:
          type === "table"
            ? `Table ${input}`
            : type === "room"
            ? `Room ${input}`
            : "Parcel Order",
        source, // pass source through
        discountPercent: Number(discountPercent || 0), // PASS discount to API/backend
      });
      setShowParcelModal(false);
    } catch (err) {
      console.error("Failed to place order:", err);
      alert("Failed to place order.");
    }
  }

  // Unlock audio playback on first user interaction
  useEffect(() => {
    const unlockAudio = () => {
      const audio = new window.Audio("/assets/NewOrderReceived.mp3");
      audio.muted = true;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.muted = false;
          audioUnlockedRef.current = true;
          console.log("Audio unlocked!");
          window.removeEventListener("click", unlockAudio);
        })
        .catch((e) => {
          console.log("Audio unlock failed:", e);
          window.removeEventListener("click", unlockAudio);
        });
    };
    window.addEventListener("click", unlockAudio);
    return () => window.removeEventListener("click", unlockAudio);
  }, []);

  const refetchOrders = async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      // Always send full UTC day range if date filter is set
      if (startDate && endDate) {
        const utcStart = getUTCStartOfDay(startDate);
        const utcEnd = getUTCEndOfDay(endDate);
        const orders = await fetchOrders(
          restaurantId,
          utcStart.toISOString(),
          utcEnd.toISOString()
        );
        setOrders(orders);
      } else {
        const orders = await fetchOrders(restaurantId);
        setOrders(orders);
      }
    } catch (err) {
      setError("Failed to fetch orders");
    }
    setLoading(false);
  };

  if (!restaurantId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            No Restaurant Selected
          </h2>
          <p className="text-gray-600">
            Please select a restaurant to view orders
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-3 sm:p-4 md:p-8 rounded-xl sm:rounded-2xl">
      {/* Print Modal */}
      {showPrintModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowPrintModal(false);
          }}
        >
          <div
            className="bg-white rounded-lg shadow-lg p-4 sm:p-6 max-w-4xl w-full overflow-auto"
            style={{ maxHeight: "90vh" }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg sm:text-xl font-bold">
                Printable Orders Report
              </h2>
              <button
                className="text-xl sm:text-2xl text-gray-400 hover:text-gray-700"
                onClick={() => setShowPrintModal(false)}
              >
                &times;
              </button>
            </div>
            <div id="printable-orders-table" className="overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <thead>
                  <tr>
                    <th className="border px-2 py-1 text-xs sm:text-sm">
                      Order ID
                    </th>
                    <th className="border px-2 py-1 text-xs sm:text-sm">
                      Table
                    </th>
                    <th className="border px-2 py-1 text-xs sm:text-sm">
                      Status
                    </th>
                    <th className="border px-2 py-1 text-xs sm:text-sm">
                      Total
                    </th>
                    <th className="border px-2 py-1 text-xs sm:text-sm">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="border px-2 py-1 text-xs sm:text-sm">
                        #{order.id.slice(-5)}
                      </td>
                      <td className="border px-2 py-1 text-xs sm:text-sm">
                        {order.tableName || `Table ${order.tableId}`}
                      </td>
                      <td className="border px-2 py-1 text-xs sm:text-sm">
                        {order.status}
                      </td>
                      <td className="border px-2 py-1 text-xs sm:text-sm">
                        ₹{Math.round(order.total * 1.05 * 100) / 100}
                      </td>
                      <td className="border px-2 py-1 text-xs sm:text-sm">
                        {getOrderDate(order.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              className="mt-4 px-3 sm:px-4 py-2 bg-gradient-to-r from-rose-500 to-amber-500 text-white rounded font-semibold text-sm sm:text-base"
              onClick={() => {
                const printContents = document.getElementById(
                  "printable-orders-table"
                ).innerHTML;
                const win = window.open("", "", "width=900,height=700");
                win.document.write(
                  "<html><head><title>Orders Report</title></head><body>" +
                    printContents +
                    "</body></html>"
                );
                win.document.close();
                win.print();
              }}
            >
              Print
            </button>
          </div>
        </div>
      )}
      <InvoiceModal
        order={invoiceOrder}
        onClose={() => setInvoiceOrder(null)}
        restaurantName={restaurantName}
        onAfterPrint={refetchOrders}
      />
      {/* <NewOrderReceivedModal
        order={newOrderModal}
        onClose={() => setNewOrderModal(null)}
      /> */}
      <AnimatePresence>
        <ParcelOrderModal
          show={!!updatingOrder}
          onClose={() => setUpdatingOrder(null)}
          restaurantId={restaurantId}
          title={`Update Order #${updatingOrder?.id.slice(-5)}`}
          onSubmit={handleUpdateOrder}
          submitText="Confirm Update"
          initialCart={updatingOrder?.items || []}
          initialType={
            updatingOrder?.roomNumber
              ? "room"
              : updatingOrder?.tableName &&
                updatingOrder.tableName.toLowerCase().includes("table")
              ? "table"
              : "parcel"
          }
          initialValue={
            updatingOrder?.roomNumber ||
            (updatingOrder?.tableName &&
              updatingOrder.tableName.replace(/[^0-9]/g, "")) ||
            ""
          }
        />
        <ParcelOrderModal
          show={showParcelModal}
          onClose={() => setShowParcelModal(false)}
          restaurantId={restaurantId}
          title="Create Order"
          onSubmit={handlePlaceParcelOrder}
          submitText="Place Order"
        />
      </AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-6xl mx-auto"
      >
        {/* Header Controls Row */}
        <div className="max-w-6xl mx-auto mb-4 sm:mb-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4 w-full">
            {/* Search Bar */}
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Order ID or Table"
              className="border rounded px-3 py-2 flex-1 min-w-[120px] text-sm focus:ring-2 focus:ring-rose-300"
              style={{ maxWidth: 200 }}
            />
            {/* Create Order Button */}
            <button
              className="bg-gradient-to-r from-rose-500 to-amber-500 text-white font-semibold rounded-full w-10 h-10 shadow hover:from-rose-600 hover:to-amber-600 transition flex items-center justify-center"
              onClick={() => {
                console.log("restaurantId for MenuModal:", restaurantId);
                setShowParcelModal(true);
              }}
              title="Create Order"
            >
              <FaPlus className="text-lg" />
            </button>
            {/* All Orders Filter */}
            <div className="relative">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="appearance-none bg-white border border-gray-200 rounded-full pl-3 pr-7 py-2 focus:outline-none focus:ring-2 focus:ring-rose-300 min-w-[100px] text-sm"
              >
                <option value="all">All Orders ({orders.length})</option>
                <option value="pending">
                  Pending ({statusCounts.pending || 0})
                </option>
                <option value="completed">
                  Completed ({statusCounts.completed || 0})
                </option>
              </select>
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {/* Calendar Date Range */}
            <div className="flex items-center gap-1">
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText="Start Date"
                className="border rounded px-2 py-2 min-w-[80px] text-sm"
                maxDate={endDate || new Date()}
                isClearable
              />
              <span className="text-gray-500 text-xs">to</span>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={new Date()}
                placeholderText="End Date"
                className="border rounded px-2 py-2 min-w-[80px] text-sm"
                isClearable
              />
            </div>
            {/* Print Button */}
            <button
              className="bg-gradient-to-r from-amber-400 to-rose-400 text-white font-semibold rounded-full w-10 h-10 shadow hover:from-amber-500 hover:to-rose-500 transition flex items-center justify-center"
              onClick={() => setShowPrintModal(true)}
              title="Print Orders"
            >
              <FaPrint className="text-lg" />
            </button>
            {/* CSV Export Button */}
            <button
              className="bg-green-500 text-white font-semibold rounded px-3 py-2 shadow hover:bg-green-600 transition text-sm flex items-center gap-1"
              onClick={() => exportOrdersToCSV(filteredOrders)}
              title="Export CSV"
            >
              <FaFileCsv className="inline-block" /> CSV
            </button>
          </div>
        </div>
        {loading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-center items-center py-12"
          >
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-rose-500"></div>
          </motion.div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow text-center"
          >
            <div className="text-red-500 mb-2 text-sm sm:text-base">
              {error}
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-3 sm:px-4 py-2 bg-gradient-to-r from-rose-400 to-amber-400 text-white rounded-full text-sm font-medium"
            >
              Try Again
            </button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden relative"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 text-xs">
                  <tr>
                    <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                      Table
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    {/* <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Date</th> */}
                    <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                    <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 text-xs">
                  {visibleOrders
                    .filter((order) => order.id)
                    .map((order) => (
                      <motion.tr
                        key={order.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                        className="hover:bg-rose-50 transition-colors"
                      >
                        <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                          <div className="text-xs font-medium text-gray-900">
                            #{order.id.slice(-5)}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                          <div className="text-xs text-gray-900 font-semibold">
                            {order.roomNumber ? (
                              <span>Room: {order.roomNumber}</span>
                            ) : (
                              order.tableName || `Table ${order.tableId}`
                            )}
                            {order.phoneNumber && (
                              <div className="text-xs text-gray-500">
                                Phone: {order.phoneNumber}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                          <StatusBadge status={order.status} />
                        </td>
                        <td className="px-2 sm:px-3 py-2 whitespace-nowrap font-semibold text-rose-600">
                          {(() => {
                            const grandTotal =
                              Math.round(order.total * 1.05 * 100) / 100;
                            return `₹${grandTotal.toFixed(2)}`;
                          })()}
                        </td>
                        {/* <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                        <div className="text-xs text-gray-500">{getOrderDate(order.createdAt || order.updatedAt || Date.now())}</div>
                      </td> */}
                        <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs font-medium">
                          <div className="flex gap-1 sm:gap-2">
                            <motion.button
                              className="text-blue-500 hover:text-blue-700 p-1"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              title="Update Order"
                              onClick={() => setUpdatingOrder(order)}
                            >
                              <svg
                                className="w-4 h-4 sm:w-5 sm:h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </motion.button>
                            <motion.button
                              className="text-rose-500 hover:text-rose-700 p-1"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              title="Mark as Completed"
                              onClick={() => handleMarkCompleted(order.id)}
                              disabled={
                                order.status.toLowerCase() === "completed"
                              }
                            >
                              <svg
                                className="w-4 h-4 sm:w-5 sm:h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            </motion.button>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs font-medium">
                          <motion.button
                            className="text-amber-500 hover:text-amber-700 p-1"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Show Invoice"
                            onClick={() => setInvoiceOrder(order)}
                          >
                            <svg
                              className="w-4 h-4 sm:w-5 sm:h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 17v-2a2 2 0 012-2h2a2 2 0 012 2v2m-6 4h6a2 2 0 002-2v-5a2 2 0 00-2-2H7a2 2 0 00-2 2v5a2 2 0 002 2z"
                              />
                            </svg>
                          </motion.button>
                        </td>
                      </motion.tr>
                    ))}
                </tbody>
              </table>
            </div>
            {/* Show More / Show Less Buttons */}
            <div className="flex justify-end gap-2 p-3 sm:p-4">
              {visibleCount < filteredOrders.length && (
                <button
                  className="px-3 sm:px-4 py-2 bg-gradient-to-r from-amber-400 to-rose-400 text-white rounded font-semibold shadow hover:from-amber-500 hover:to-rose-500 transition text-sm"
                  onClick={() => setVisibleCount((c) => c + 10)}
                >
                  Show More
                </button>
              )}
              {visibleCount > 10 && (
                <button
                  className="px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded font-semibold shadow hover:bg-gray-300 transition text-sm"
                  onClick={() => setVisibleCount(10)}
                >
                  Show Less
                </button>
              )}
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
